import { canNavigate } from '@layouts/plugins/casl'
import { destr } from 'destr'

// Helper para leer cookies directamente de document.cookie
// Esto evita problemas con useCookie que puede no leer correctamente cookies después de un reload
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper para leer y decodificar cookies JSON (como userData)
const getCookieJSON = (name) => {
  const cookieValue = getCookie(name);
  if (!cookieValue) return null;
  try {
    // useCookie codifica con encodeURIComponent y luego JSON.stringify
    // Necesitamos decodificar de la misma manera
    return destr(decodeURIComponent(cookieValue));
  } catch (e) {
    return null;
  }
}

export const setupGuards = router => {
  // 👉 router.beforeEach
  // Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
  router.beforeEach((to, from, next) => {
    /*
         * If it's a public route, continue navigation. This kind of pages are allowed to visited by login & non-login users. Basically, without any restrictions.
         * Examples of public routes are, 404, under maintenance, etc.
         */
    if (to.meta.public) {
      next()
      return
    }

    /**
         * Check if user is logged in by checking if token & user data exists
         * Leemos ambas cookies directamente de document.cookie para evitar problemas con useCookie
         * que puede no leer correctamente cookies después de un reload normal
         */
    const accessToken = getCookie('accessToken');
    let userData = getCookieJSON('userData');
    
    // Si no pudimos leer userData directamente, intentar con useCookie como fallback
    if (!userData && accessToken) {
      try {
        const userDataCookie = useCookie('userData');
        userData = userDataCookie?.value || null;
      } catch (e) {
        // Ignorar errores
      }
    }
    
    const isLoggedIn = !!(userData && accessToken)

    /*
          If user is logged in and is trying to access login like page, redirect to home
          else allow visiting the page
          (WARN: Don't allow executing further by return statement because next code will check for permissions)
         */
    if (to.meta.unauthenticatedOnly) {
      if (isLoggedIn) {
        next('/')
        return
      } else {
        next()
        return
      }
    }
    // Verificar permisos solo si la ruta tiene meta.action o meta.subject definidos
    // Si no tiene, permitir la navegación si el usuario está autenticado
    const hasPermissionMeta = to.matched.some(route => route.meta?.action || route.meta?.subject);
    
    if (hasPermissionMeta) {
      // Solo verificar canNavigate si hay permisos definidos
      try {
        if (!canNavigate(to)) {
          /* eslint-disable indent */
                if (isLoggedIn) {
                  next({ name: 'not-authorized' })
                } else {
                  next({
                    name: 'login',
                    query: {
                      ...to.query,
                      to: to.fullPath !== '/' ? to.path : undefined,
                    },
                  })
                }
                /* eslint-enable indent */
                return
        }
      } catch (e) {
        // Si canNavigate falla (por ejemplo, por problemas de inicialización),
        // permitir la navegación si el usuario está autenticado
        if (!isLoggedIn) {
          next({
            name: 'login',
            query: {
              ...to.query,
              to: to.fullPath !== '/' ? to.path : undefined,
            },
          })
          return
        }
      }
    } else {
      // Si no hay permisos definidos pero el usuario no está autenticado, redirigir a login
      if (!isLoggedIn) {
        next({
          name: 'login',
          query: {
            ...to.query,
            to: to.fullPath !== '/' ? to.path : undefined,
          },
        })
        return
      }
    }
    
    // Si llegamos aquí, permitir la navegación
    next()
  })
}
